import { createFileRoute, Link } from "@tanstack/react-router";
import { Callout } from "@/components/calv/Callout";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { SingleCase } from "@/components/calv/SingleCase";
import { EngagementGrid } from "@/components/calv/EngagementGrid";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { DisciplineGrid } from "@/components/calv/DisciplineGrid";
import { relatedBySource } from "@/lib/related-services-data";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/performance-marketing/")({
  head: () => {
    const h = pageHead("performanceMarketing", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
            jsonLdScript(serviceSchema({
              name: "Performance Marketing",
              description: "Search, social media, and affiliate performance across Google Ads, Meta, TikTok, LinkedIn and Microsoft Ads, run hands-on by the managing director.",
              url: "/en/services/performance-marketing",
            })),
            jsonLdScript(breadcrumbSchema([
              { name: "Start", path: "/en" },
              { name: "Services", path: "/en/services" },
              { name: "Performance Marketing", path: "/en/services/performance-marketing" },
            ])),
          ],
    };
  },
  component: PerformanceMarketingPage,
});

const steps = [
  { num: "01", title: "Audit & data foundation", body: "Account, tracking and competitive analysis. Data gaps are closed before anything else happens." },
  { num: "02", title: "Hypothesis setup", body: "Calibrating industry, account and client- and account-specific hypotheses. Setting confidence thresholds and escalation paths." },
  { num: "03", title: "Live control", body: "Daily monitoring, rule-based auto-adjustments below threshold, senior review on anomalies — the MD sits on the account." },
  { num: "04", title: "Weekly review", body: "Results review, hypothesis verification, roadmap adjustment. Four eyes minimum, every action traceable." },
];

const capabilities = [
  { name: "Google Ads — Search", detail: "Brand · Non-Brand · Long-Tail" },
  { name: "Google Ads — Shopping & PMax", detail: "Feed hygiene · Asset Group discipline" },
  { name: "YouTube & Demand Gen", detail: "Awareness · View-Through (in Google Ads)" },
  { name: "Meta Ads", detail: "Facebook · Instagram · Advantage+" },
  { name: "TikTok Ads", detail: "Spark Ads · Creator sourcing" },
  { name: "LinkedIn Ads", detail: "B2B · ABM · Conversation Ads" },
  { name: "Microsoft Ads", detail: "Search · Audience Network" },
  { name: "Affiliate marketing", detail: "Partner steering · tracking" },
  { name: "Server-side tracking", detail: "Enhanced Conversions · GA4" },
  { name: "Negative-list hygiene", detail: "Rule-based · daily" },
];

const stack = [
  { name: "Google Ads", small: "Search · Shopping · PMax · YouTube" },
  { name: "Meta Ads Manager", small: "Facebook · Instagram" },
  { name: "TikTok Ads", small: "incl. Events API" },
  { name: "LinkedIn Ads", small: "B2B targeting" },
  { name: "Microsoft Ads", small: "Search · UET" },
  { name: "Google Analytics 4", small: "+ BigQuery Export" },
  { name: "Google Tag Manager", small: "server & web" },
  { name: "Delta", small: "Feed management · proprietary" },
];

const engagements = [
  { label: "Media budget", title: "From €3,500 / month", body: "Below this threshold engine-driven optimization doesn't pay back. We'll honestly suggest other routes if you're under it." },
  { label: "Lead time", title: "Four to six weeks", body: "Audit, tracking setup and engine calibration take time. We don't make shorter promises." },
  { label: "Minimum term", title: "Four quarters", body: "Systemic compound effects only show after three months. Four quarters gives the levers time to fully play out." },
  { label: "Fee model", title: "Efficiency share + base fee", body: (<>Fixed senior fee plus a share of measurable efficiency gains — <strong className="text-ink">not</strong> of media spend. Aligns our incentive with yours.</>) },
];

function PerformanceMarketingPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing"
          headline={<>Performance marketing as a <em>steering system</em>, not a cost line.</>}
          lead="We run SEA, Shopping, and Paid Social across Google Ads, Meta, TikTok, LinkedIn, and Microsoft Ads — as one integrated steering system with shared tracking substance, pacing discipline, and senior review. Suited to companies that want their performance marketing out of the black box."
          metaStrip={[
            "€10–200k monthly ad budgets",
            "Senior review",
            "Server-side tracking as standard",
            "MD hands-on",
          ]}
          primaryCta={{
            eyebrow: "Free · approx. 30 minutes",
            label: "Request a strategy conversation",
            to: "/en/contact",
          }}
          secondaryCta={{
            label: "Or: download the Performance Marketing Checklist without signup",
            to: "/en/tools/performance-marketing-checklist",
          }}
          breadcrumbs={[
            { label: "Start", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing" },
          ]}
        />

        <Section variant="compact">
          <Callout>
            <strong>In context:</strong> Performance marketing works cross-channel with a unified methodology. Anyone looking specifically for the integrated social methodology — organic, paid, and community as one system across Meta, TikTok, LinkedIn, and Pinterest — will find it under <Link to="/en/services/social-media-marketing" className="underline">Social Media Marketing →</Link>.
          </Callout>
        </Section>


        <Section number="02" kicker="Standpoint" headline={<>What we do <em>differently</em> in performance marketing.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Most media budgets flow into accounts that "someone looks at once or twice a week". Detail movements — competitive drift, search-term anomalies, time-of-day conversion shifts — go unnoticed. The result: high click costs without corresponding leverage.</p>
              <p className="mb-5">We treat performance marketing as <strong className="font-semibold text-ink">continuous system control</strong>. Account hygiene, competitive drift and search-term anomalies are checked daily, rule-based adjustments run automatically, anomalies escalate directly to the managing director — no hand-off. This shifts the human contribution to where it creates value: strategy and exceptional decisions, not routine maintenance.</p>
              <p><strong className="font-semibold text-ink">On pricing:</strong> We <em>do not</em> participate in your media budget on principle — that coupling creates incentives that don't align with yours. If you wish, a component of our fee can be tied to the measurably generated savings. That gives us the same incentive as you: as much impact per euro as possible.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="03" kicker="Performance marketing in detail"
          headline={<>Seven disciplines, <em>seven detail pages.</em></>}
          caption="Each performance channel works by its own logic. Anyone wanting to manage economically needs channel-specific depth — not generic lists. Here you'll find the individual disciplines with their Calvarius methodology.">
          <DisciplineGrid
            items={[
              { eyebrow: "SEARCH · SHOPPING · PMAX", title: "Google Ads", subtitle: "Economically managed, not platform-driven", href: "/en/services/performance-marketing/google-ads" },
              { eyebrow: "FACEBOOK · INSTAGRAM", title: "Meta Ads", subtitle: "Performance beyond platform logic, with creative discipline", href: "/en/services/performance-marketing/meta-ads" },
              { eyebrow: "B2B · ABM · LEAD GEN", title: "LinkedIn Ads", subtitle: "B2B performance with pipeline discipline", href: "/en/services/performance-marketing/linkedin-ads" },
              { eyebrow: "SPARK ADS · CREATOR", title: "TikTok Ads", subtitle: "Visual performance with Spark Ad discipline", href: "/en/services/performance-marketing/tiktok-ads" },
              { eyebrow: "BING · LINKEDIN PROFILE", title: "Microsoft Ads", subtitle: "Search complement for DACH", href: "/en/services/performance-marketing/microsoft-ads" },
              { eyebrow: "AWARENESS · VIDEO", title: "YouTube & Demand Gen", subtitle: "Awareness with performance logic", href: "/en/services/performance-marketing/youtube-demand-gen" },
              { eyebrow: "PARTNER · TRACKING", title: "Affiliate Marketing", subtitle: "Partner steering with tracking discipline", href: "/en/services/performance-marketing/affiliate-marketing" },
            ]}
          />
        </Section>

        <Section variant="tinted" number="04" kicker="Approach" headline={<>Four phases that <em>never really end.</em></>}
          caption="The collaboration isn't a project with a start and an end, it's a continuous loop. Phase one runs once, phases two through four rotate weekly.">
          <ProcessFlow steps={steps} />
        </Section>

        <Section number="05" kicker="Capabilities & stack" headline={<>What's concretely <em>steerable</em> in every collaboration.</>}
          caption="Each item below is a lever we move in active client projects. Not everything fits every client — but everything is on the table.">
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
          <div className="mt-12 rounded-lg border border-rule bg-bg-subtle p-7">
            <p className="eyebrow mb-4">Proprietary tools used</p>
            <div className="flex flex-wrap gap-3">
              <a href="/en/services/delta" className="rounded-md border border-rule bg-white px-4 py-2 font-mono text-xs uppercase tracking-wider text-ink-2 hover:border-cyan hover:text-cyan-deep">Delta — Feed management →</a>
              <a href="/en/services/picazzoo" className="rounded-md border border-rule bg-white px-4 py-2 font-mono text-xs uppercase tracking-wider text-ink-2 hover:border-cyan hover:text-cyan-deep">picazzoo — Creative production →</a>
              <a href="/en/services/gonzalads" className="rounded-md border border-rule bg-white px-4 py-2 font-mono text-xs uppercase tracking-wider text-ink-2 hover:border-cyan hover:text-cyan-deep">GonzalAds — Meta campaigns →</a>
            </div>
          </div>
        </Section>

        <Section variant="tinted" number="06" kicker="Pattern" headline={<>What becomes possible when you <em>stop guessing.</em></>}
          caption="A typical constellation from our B2B and SaaS practice — described anonymously.">
          <SingleCase
            meta="Pattern · B2B · SaaS"
            title="Volume grows, qualified pipeline doesn't."
            body={<>
              <p>Starting point: high Google Ads spend, but unclear pipeline contribution. CRM and ad account run separately, lead quality stays invisible in reporting.</p>
              <p>Intervention: rebuilt server-side tracking, integrated lead scoring directly into Google Ads, targeted content investments in bottom-funnel clusters — rebalanced daily through CRM data.</p>
            </>}
            statValue="MD"
            statLabel={<>operationally on the account<br />no hand-off to juniors</>}
          />
        </Section>

        <LogoReferences locale="en" number="07" />

        <RelatedServices
          locale="en"
          fromId="performance-marketing"
          number="08"
          items={relatedBySource["performance-marketing"]}
        />

        <Section number="09" kicker="Engagement" headline={<>What performance marketing <em>realistically requires.</em></>}>
          <EngagementGrid items={engagements} />
          <div className="mt-12">
            <CTABlock
              headline={<>Is your <em>performance budget</em> economically steered?</>}
              body="Want to know which parts of your performance budget are actually working – and which aren't?"
              primary={{ label: "Get in touch", to: "/en/contact" }}
              secondary={{ label: "All services", to: "/en/services" }}
            />
          </div>
        </Section>
        <GlossaryTerms locale="en" ids={["performance-marketing", "cpa", "cpc", "attribution-model"]} />
      </main>
      <Footer />
    </>
  );
}
